import { useCallback, useEffect, useRef, useState } from 'react';
import useDarkMode from '../../hooks/useDarkMode';
import { useIntl } from 'react-intl';

interface IBudgetSelectCustomProps {
  value: string[];
  onChange: (option: { id: string; label: string; [key: string]: any } | undefined) => void;
  loadData: (
    searchTerm: string,
    pageSize: number,
    page: number,
  ) => Promise<Array<{ id: string; label: string; [key: string]: any }>>;
  placeholder?: string;
  label?: string | React.ReactNode;
  initialLimit?: number;
  disabled?: boolean;
  className?: string;
  isRemoved?: boolean;
  loadOnFocus?: boolean;
  autoFocus?: boolean;
  openOnFocus?: boolean;
  inputBorderColor?: string;
  inputBackgroundColor?: string;
  dropdownBackgroundColor?: string;
  highlightedOptionColor?: string;
  selectedOptionColor?: string;
  readOnly?: boolean;
}

const SelectCustom = (props: IBudgetSelectCustomProps) => {
  const intl = useIntl();
  const [filteredOptions, setFilteredOptions] = useState<
    Array<{ id: string; label: string; [key: string]: any }>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [displayedCount, setDisplayedCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [hasOptions, setHasOptions] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const allDataRef = useRef<Array<{ id: string; label: string; [key: string]: any }>>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const hasLoadedOnceRef = useRef<boolean>(false);
  const isLoadingRef = useRef<boolean>(false);
  const isClickingArrowRef = useRef<boolean>(false);
  const currentPageRef = useRef<number>(1);
  const limit = props.initialLimit || 10;
  const isDarkMode = useDarkMode();

  const defaultInputBorderColor = props.inputBorderColor || (isDarkMode ? '#525252' : '#e5e7eb');
  const defaultInputBackgroundColor = props.inputBackgroundColor || (isDarkMode ? '#262626' : '#ffffff');
  const defaultDropdownBackgroundColor = props.dropdownBackgroundColor || (isDarkMode ? '#262626' : '#ffffff');
  const defaultHighlightedOptionColor = props.highlightedOptionColor || (isDarkMode ? '#1d4ed8' : '#3b82f6');
  const defaultSelectedOptionColor = props.selectedOptionColor || (isDarkMode ? '#404040' : '#e5e7eb');

  const loadInitialData = useCallback(async () => {
    if (isLoadingRef.current) {
      return;
    }
    
    if (props.loadOnFocus && hasLoadedOnceRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const data = await props.loadData('', limit, 0);
      allDataRef.current = data;
      currentPageRef.current = 1;
      setFilteredOptions(data);
      setDisplayedCount(data.length);
      setHasMore(data.length >= limit);
      setHasOptions(data.length > 0);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      console.error('[SelectCustom] loadInitialData - erro', error);
      setFilteredOptions([]);
      setHasMore(false);
      setHasOptions(false);
      hasLoadedOnceRef.current = true;
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [props.loadData, limit, props.loadOnFocus]);

  useEffect(() => {
    if (!props.loadOnFocus) {
      loadInitialData();
    }
  }, [loadInitialData, props.loadOnFocus]);

  useEffect(() => {
    if (props.autoFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [props.autoFocus]);

  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isSearching) {
      updateDropdownPosition();
    }
  }, [isSearching, updateDropdownPosition]);

  useEffect(() => {
    if (!isSearching) return;
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isSearching, updateDropdownPosition]);

  useEffect(() => {
    setHighlightedIndex(-1);
    optionRefs.current = [];
  }, [filteredOptions]);

  useEffect(() => {
    const optionElement = optionRefs.current[highlightedIndex];
    const dropdownElement = dropdownRef.current;

    if (highlightedIndex >= 0 && optionElement && dropdownElement) {
      const optionTop = optionElement.offsetTop;
      const optionBottom = optionTop + optionElement.offsetHeight;
      const dropdownTop = dropdownElement.scrollTop;
      const dropdownBottom = dropdownTop + dropdownElement.offsetHeight;

      if (optionTop < dropdownTop) {
        dropdownElement.scrollTop = optionTop;
      } else if (optionBottom > dropdownBottom) {
        dropdownElement.scrollTop = optionBottom - dropdownElement.offsetHeight;
      }
    }
  }, [highlightedIndex]);

  const handleSearch = useCallback(
    async (term: string) => {
      console.log(`term1`, term)
      setSearchTerm(term);
      setHighlightedIndex(-1);

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(async () => {
        if (props.loadOnFocus && !hasLoadedOnceRef.current) {
          return;
        }

        setLoading(true);

        try {
          if (term) {
            console.log(`term2`, term)
            const data = await props.loadData(term, limit, 0);
            allDataRef.current = data;
            currentPageRef.current = 0;

            setFilteredOptions(data);
            setDisplayedCount(data.length);
            setHasMore(data.length >= limit);
            setHasOptions(data.length > 0);
            if (data.length > 0) {
              setHighlightedIndex(0);
            }
          } else {
            if (props.loadOnFocus && hasLoadedOnceRef.current) {
              setFilteredOptions(allDataRef.current);
              setDisplayedCount(allDataRef.current.length);
              setHasMore(allDataRef.current.length >= limit);
              setHasOptions(allDataRef.current.length > 0);
              if (allDataRef.current.length > 0) {
                setHighlightedIndex(0);
              }
            } else {
              const data = await props.loadData('', limit, 0);
              allDataRef.current = data;
              currentPageRef.current = 0;
              setFilteredOptions(data);
              setDisplayedCount(data.length);
              setHasMore(data.length >= limit);
              setHasOptions(data.length > 0);
              if (data.length > 0) {
                setHighlightedIndex(0);
              }
            }
          }
        } catch (error) {
          setFilteredOptions([]);
          setHasMore(false);
          setHasOptions(false);
          setHighlightedIndex(-1);
        } finally {
          setLoading(false);
        }
      }, 300);
    },
    [props.loadData, limit, props.loadOnFocus],
  );

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    try {
      const nextPage = currentPageRef.current + 1;
      const data = await props.loadData(searchTerm, limit, nextPage);

      if (data.length > 0) {
        currentPageRef.current = nextPage;
        allDataRef.current = [...allDataRef.current, ...data];
        setFilteredOptions((prev) => [...prev, ...data]);
        setDisplayedCount((prev) => prev + data.length);
      }

      setHasMore(data.length >= limit);
    } catch (error) {
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, props.loadData, searchTerm, limit]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const selectedOptionId = props.value[0] || '';
  const [selectedOptionLabel, setSelectedOptionLabel] = useState<string>('');

  useEffect(() => {
    const findSelectedOption = () => {
      if (selectedOptionId) {
        if (selectedOptionId === 'CORRETIVA') {
          setSelectedOptionLabel('Corretiva');
          return;
        }
        if (selectedOptionId === 'DESGASTE') {
          setSelectedOptionLabel('Desgaste');
          return;
        }
        
        const allOptions = [...filteredOptions, ...allDataRef.current];
        const foundInCurrent = allOptions.find((opt) => opt.id === selectedOptionId);

        if (foundInCurrent) {
          const label = foundInCurrent.label;
          const labelWithoutCode = label.includes(' (') ? label.split(' (')[0] : label;
          setSelectedOptionLabel(labelWithoutCode);
        } else {
          setSelectedOptionLabel(selectedOptionId);
        }
      } else {
        setSelectedOptionLabel('');
      }
    };
    findSelectedOption();
  }, [selectedOptionId, filteredOptions]);

  const displayValue = isSearching ? searchTerm : selectedOptionLabel;

  const selectOptionAndClose = (option: any) => {
    let displayLabel = option.label;
    if (option.id === 'CORRETIVA') {
      displayLabel = 'Corretiva';
    } else if (option.id === 'DESGASTE') {
      displayLabel = 'Desgaste';
    }
    setSelectedOptionLabel(displayLabel);
    props.onChange(option);
    setIsSearching(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const focusNextInput = (currentTarget: HTMLInputElement) => {
    const form = currentTarget.form;
    if (!form) {
      return;
    }
    const inputs = Array.from(form.querySelectorAll('input, select, textarea, button')).filter(
      (el) =>
        !(el as HTMLElement).hasAttribute('disabled') &&
        !(el as HTMLElement).hasAttribute('readonly'),
    ) as HTMLElement[];
    const currentIndex = inputs.indexOf(currentTarget);
    const nextIndex = currentIndex + 1;
    if (nextIndex < inputs.length) {
      inputs[nextIndex].focus();
    }
  };

  const handleTabKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSearching) {
      return;
    }
    
    e.preventDefault();
    
    if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
      const option = filteredOptions[highlightedIndex];
      selectOptionAndClose(option);
    } else if (searchTerm && searchTerm.trim() !== '') {
      const trimmedValue = searchTerm.trim();
      const foundOption = filteredOptions.find(
        (opt) => opt.id.toLowerCase() === trimmedValue.toLowerCase() || 
                 opt.label.toLowerCase() === trimmedValue.toLowerCase()
      );
      
      if (foundOption) {
        selectOptionAndClose(foundOption);
      } else {
        props.onChange({ id: trimmedValue, label: trimmedValue });
        setSelectedOptionLabel(trimmedValue);
        setIsSearching(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    }
    
    focusNextInput(e.target as HTMLInputElement);
  };

  const handleArrowDownWhenNotSearching = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsSearching(true);
    setSearchTerm(selectedOptionLabel);
    if (filteredOptions.length > 0) {
      setHighlightedIndex(0);
    }
  };

  const handleArrowDown = () => {
    setHighlightedIndex((prev) => {
      const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
      return Math.max(nextIndex, 0);
    });
  };

  const handleArrowUp = () => {
    setHighlightedIndex((prev) => {
      const nextIndex = prev > 0 ? prev - 1 : 0;
      return nextIndex;
    });
  };

  const handleEnterKey = () => {
    if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
      selectOptionAndClose(filteredOptions[highlightedIndex]);
    } else if (searchTerm && searchTerm.trim() !== '') {
      const trimmedValue = searchTerm.trim();
      const foundOption = filteredOptions.find(
        (opt) => opt.id.toLowerCase() === trimmedValue.toLowerCase() || 
                 opt.label.toLowerCase() === trimmedValue.toLowerCase()
      );
      
      if (foundOption) {
        selectOptionAndClose(foundOption);
      } else {
        props.onChange({ id: trimmedValue, label: trimmedValue });
        setSelectedOptionLabel(trimmedValue);
        setIsSearching(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    }
  };

  const handleEscapeKey = () => {
    setIsSearching(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      handleTabKey(e);
      return;
    }

    if (!isSearching && e.key === 'ArrowDown') {
      handleArrowDownWhenNotSearching(e);
      return;
    }

    if (!isSearching) {
      return;
    }

    if (loading) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        if (filteredOptions.length > 0) {
          e.preventDefault();
          handleArrowDown();
        }
        break;
      case 'ArrowUp':
        if (filteredOptions.length > 0) {
          e.preventDefault();
          handleArrowUp();
        }
        break;
      case 'Enter':
        e.preventDefault();
        handleEnterKey();
        break;
      case 'Escape':
        e.preventDefault();
        handleEscapeKey();
        break;
      default:
        break;
    }
  };

  const getInputStyle = () => {
    const baseStyle: React.CSSProperties = {
      borderColor: defaultInputBorderColor,
      backgroundColor: props.isRemoved ? (isDarkMode ? '#450a0a' : '#fef2f2') : defaultInputBackgroundColor,
    };
    return baseStyle;
  };

  return (
    <div 
    // style={{ position: 'relative', zIndex: 1 }}
    >
      {props.label && <div className="font-bold mb-2 text-size-12 text-gray-500 dark:text-neutral-400 whitespace-nowrap">{props.label}</div>}
      <div className="relative" ref={containerRef}>
        <input
          ref={inputRef}
          type="text"
          className={`form-control w-full pr-8 z-1
            ${props.readOnly ? 'caret-transparent' : ''}
            ${props.className || ''} ${props.isRemoved ? '!bg-red-50 dark:!bg-red-950' : ''} ${props.disabled ? '!bg-white dark:!bg-neutral-800 !border-gray-300 dark:!border-neutral-700' : ''}
          `}
          placeholder={props.placeholder || intl.formatMessage({ id: 'intl-msg:selectCustom.selectCustom.placeholder' })}
          value={displayValue}
          disabled={props.disabled}
          readOnly={props.readOnly}
          style={getInputStyle()}
          onChange={(e) => {

            if (props.disabled) return;
            const newValue = e.target.value;
            if (newValue === '') {
              setSelectedOptionLabel('');
              props.onChange(undefined);
              setSearchTerm('');
              handleSearch('');
              if (!(props.loadOnFocus && !hasLoadedOnceRef.current)) {
                setIsSearching(true);
                handleSearch('');
              } else if (props.loadOnFocus && !hasLoadedOnceRef.current) {
                setIsSearching(true);
              }
            } else if (!(props.loadOnFocus && !hasLoadedOnceRef.current)) {
              setIsSearching(true);
              setHasOptions(true);
              handleSearch(newValue);
            } else {
              setSelectedOptionLabel(newValue);
              setSearchTerm(newValue);
              props.onChange({ id: newValue, label: newValue });
            }
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            const shouldOpenOnFocus = props.openOnFocus !== false;
            
            if (props.loadOnFocus && !hasLoadedOnceRef.current && !isLoadingRef.current) {
              loadInitialData().then(() => {
                if (shouldOpenOnFocus) {
                  setIsSearching(true);
                  setSearchTerm(selectedOptionLabel);
                  if (allDataRef.current.length > 0) {
                    setFilteredOptions(allDataRef.current.slice(0, limit));
                    setHasOptions(true);
                    setHighlightedIndex(0);
                  }
                }
              });
            } else if (shouldOpenOnFocus) {
              setIsSearching(true);
              setSearchTerm(selectedOptionLabel);
              if (filteredOptions.length > 0) {
                setHighlightedIndex(0);
              } else if (allDataRef.current.length > 0) {
                setFilteredOptions(allDataRef.current.slice(0, limit));
                setHasOptions(true);
                setHighlightedIndex(0);
              } else if (!hasLoadedOnceRef.current && !isLoadingRef.current) {
                loadInitialData().then(() => {
                  if (allDataRef.current.length > 0) {
                    setFilteredOptions(allDataRef.current.slice(0, limit));
                    setHasOptions(true);
                    setHighlightedIndex(0);
                  }
                });
              }
            }
          }}
          onBlur={(e) => {
            if (isClickingArrowRef.current) {
              isClickingArrowRef.current = false;
              return;
            }
            
            setIsFocused(false);
            const relatedTarget = e.relatedTarget as HTMLElement;
            const isClickingDropdown = relatedTarget?.closest('.absolute');
            
            if (!isClickingDropdown) {
              setTimeout(() => {
                setIsSearching(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
              }, 200);
            }
          }}
        />
        {!props.disabled && <div 
          className="absolute right-3 top-1/2 transform -translate-y-1/2 mr-1"
          style={{ cursor: 'pointer' }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (props.disabled) return;
            
            isClickingArrowRef.current = true;
            
            if (isSearching) {
              setIsSearching(false);
              setSearchTerm('');
              setHighlightedIndex(-1);
              setTimeout(() => {
                isClickingArrowRef.current = false;
              }, 0);
            } else {
              inputRef.current?.focus();
              
              setTimeout(async () => {
                if (!hasLoadedOnceRef.current && !isLoadingRef.current) {
                  await loadInitialData();
                }
                
                setIsSearching(true);
                setSearchTerm(selectedOptionLabel);
                
                if (filteredOptions.length > 0) {
                  setHighlightedIndex(0);
                } else if (allDataRef.current.length > 0) {
                  setFilteredOptions(allDataRef.current.slice(0, limit));
                  setHasOptions(true);
                  setHighlightedIndex(0);
                } else if (!hasLoadedOnceRef.current && !isLoadingRef.current) {
                  await loadInitialData();
                  if (allDataRef.current.length > 0) {
                    setFilteredOptions(allDataRef.current.slice(0, limit));
                    setHasOptions(true);
                    setHighlightedIndex(0);
                  }
                }
                
                setTimeout(() => {
                  isClickingArrowRef.current = false;
                }, 100);
              }, 0);
            }
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 4L6 8L10 4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>}
        {(() => {
          const shouldShow = isSearching && hasOptions && isFocused;
          if (!shouldShow) {
            return null;
          }
          return (
          <div
            ref={dropdownRef}
            className="border border-gray-200 dark:border-neutral-700 dark:text-neutral-100 rounded-md shadow-lg max-h-60 overflow-y-auto"
            style={{ 
              backgroundColor: defaultDropdownBackgroundColor,
              zIndex: 99999,
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
            role="menu"
            aria-label={intl.formatMessage({ id: 'intl-msg:selectCustom.selectCustom.optionsAriaLabel' })}
            tabIndex={-1}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {loading && <div className="p-4 text-center text-gray-500 dark:text-neutral-400">{intl.formatMessage({ id: 'intl-msg:selectCustom.selectCustom.loading' })}</div>}
            {!loading && filteredOptions.length === 0 && (
              <div className="p-4 text-center text-gray-500 dark:text-neutral-400">{intl.formatMessage({ id: 'intl-msg:selectCustom.selectCustom.noResults' })}</div>
            )}
            {!loading &&
              filteredOptions.map((option, index) => {
                const isHighlighted = highlightedIndex === index;
                const backgroundColor = isHighlighted ? defaultHighlightedOptionColor : 'transparent';
                return (
                  <button
                    key={option.id}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    type="button"
                    role="menuitem"
                    className="w-full text-left p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-neutral-700"
                    style={{
                      backgroundColor,
                      color: isHighlighted ? 'white' : 'inherit',
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      let displayLabel = option.label;
                      if (option.id === 'CORRETIVA') {
                        displayLabel = 'Corretiva';
                      } else if (option.id === 'DESGASTE') {
                        displayLabel = 'Desgaste';
                      }
                      setSelectedOptionLabel(displayLabel);
                      props.onChange(option);
                      setIsSearching(false);
                      setSearchTerm('');
                      setHighlightedIndex(-1);
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            {hasMore && !loading && (
              <button
                type="button"
                className="w-full p-2 text-gray-600 dark:text-neutral-400 underline hover:bg-gray-100 dark:hover:bg-neutral-700"
                onMouseDown={(e) => e.preventDefault()}
                onClick={loadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? intl.formatMessage({ id: 'intl-msg:selectCustom.selectCustom.loading' }) : intl.formatMessage({ id: 'intl-msg:selectCustom.selectCustom.loadMore' })}
              </button>
            )}
          </div>
          );
        })()}
      </div>
    </div>
  );
};

export default SelectCustom;

